import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, X, Clock } from "lucide-react";
import { apiClient } from "../../services/api";
import {
	CraftModal,
	CraftModalHeader,
	CraftModalBody,
	CraftModalFooter,
	CraftButton,
} from "@craft-design-system/core-components-react";

const SerieDataTableSidePanel = ({ isOpen, onClose, selectedRow, onTagsUpdated }) => {
	const [isHovered, setIsHovered] = useState(false);
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [worklogModalOpen, setWorklogModalOpen] = useState(false);
	const [fieldTrackingData, setFieldTrackingData] = useState([]);
	const [loadingFieldTracking, setLoadingFieldTracking] = useState(false);
	// Tags state
	const [tagsInput, setTagsInput] = useState("");
	const [savingTags, setSavingTags] = useState(false);

	// Function to fetch field tracking data
	const fetchFieldTracking = async (snr) => {
		if (!snr) return;

		setLoadingFieldTracking(true);
		try {
			console.log(`Fetching series field tracking for SNR: ${snr}`);
			// URL encode the snr to handle special characters
			const encodedSNR = encodeURIComponent(snr);
			const response = await apiClient.get(
				`/api/series-field-tracking/${encodedSNR}`,
			);

			console.log(`Response status: ${response.status}`);
			console.log("Series field tracking data received:", response.data);
			setFieldTrackingData(response.data.tracking_records || []);
		} catch (error) {
			console.error("Error fetching series field tracking data:", error);
			setFieldTrackingData([]);
		} finally {
			setLoadingFieldTracking(false);
		}
	};
	// Toggle worklogs visibility and fetch data when showing
	const handleToggleWorklogs = () => {
		if (selectedRow?.SNR) {
			fetchFieldTracking(selectedRow.SNR);
		}
		setWorklogModalOpen(true);
	};

	// Parse tags string into array
	const parseTags = (tagsStr) => {
		if (!tagsStr) return [];
		return tagsStr.split(",").map((t) => t.trim()).filter(Boolean);
	};

	// Save tags to backend
	const handleSaveTags = async (newTagsStr) => {
		if (!selectedRow?.SNR || !selectedRow?.project) return;
		setSavingTags(true);
		try {
			await apiClient.put("/api/update-series-tags", {
				snr: selectedRow.SNR,
				snr_anlage_projnr: selectedRow.project,
				tags: newTagsStr || null,
			});
			if (onTagsUpdated) {
				onTagsUpdated(selectedRow.SNR, selectedRow.project, newTagsStr || "");
			}
		} catch (error) {
			console.error("Error saving tags:", error);
			alert("Failed to save tags. Please try again.");
		} finally {
			setSavingTags(false);
		}
	};

	// Add a tag
	const handleAddTag = async () => {
		const newTag = tagsInput.trim();
		if (!newTag) return;
		const existing = parseTags(selectedRow?.tags || "");
		if (existing.includes(newTag)) {
			setTagsInput("");
			return;
		}
		const updated = [...existing, newTag].join(",");
		await handleSaveTags(updated);
		setTagsInput("");
	};

	// Remove a tag
	const handleRemoveTag = async (tagToRemove) => {
		const existing = parseTags(selectedRow?.tags || "");
		const updated = existing.filter((t) => t !== tagToRemove).join(",");
		await handleSaveTags(updated);
	};

	// Close panel when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (isOpen && !event.target.closest(".side-panel-container")) {
				onClose();
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen, onClose]);

	// Handle escape key to close panel
	useEffect(() => {
		const handleEscapeKey = (event) => {
			if (event.key === "Escape" && isOpen) {
				onClose();
			}
		};

		document.addEventListener("keydown", handleEscapeKey);
		return () => document.removeEventListener("keydown", handleEscapeKey);
	}, [isOpen, onClose]);
	// Clear field tracking data when selectedRow changes
	useEffect(() => {
		if (selectedRow?.SNR) {
			setFieldTrackingData([]);
			setWorklogModalOpen(false);
			setLoadingFieldTracking(false);
			setTagsInput("");
		}
	}, [selectedRow?.SNR]);

	if (!isOpen || !selectedRow) return null;

	// Debug logging
	console.log("SerieDataTableSidePanel - selectedRow:", selectedRow);

	const toggleCollapse = (e) => {
		e.stopPropagation();
		setIsCollapsed(!isCollapsed);
	};

	const handleClose = (e) => {
		e.stopPropagation();
		onClose();
	};

	const styles = {
		container: {
			position: "absolute",
			top: 0,
			right: 0,
			width: isCollapsed ? "50px" : "400px",
			height: "calc(100% - 50px)", // Adjusted to account for pagination footer (30px) + some margin
			backgroundColor: isCollapsed && isHovered ? "#e8e8e8" : "#f0f0f0",
			border: "2px solid #1e2832",
			borderRadius: "0",
			borderRight: "none",
			borderTop: "none",
			borderBottom: "none",
			boxShadow: "-4px 0 12px rgba(0, 0, 0, 0.15)",
			transition: "all 0.3s ease",
			zIndex: 1000,
			overflow: "hidden",
			fontFamily: "MAN EUROPE_Regular",
			cursor: isCollapsed ? "pointer" : "default",
			display: "flex",
			flexDirection: "column",
		},
		header: {
			display: "flex",
			alignItems: "center",
			padding: "12px",
			backgroundColor: "#1e2832",
			color: "white",
			justifyContent: isCollapsed ? "center" : "space-between",
			borderBottom: "1px solid #ddd",
			minHeight: "48px",
		},
		title: {
			fontSize: "17px",
			fontFamily: "MAN EUROPE_Bold",
			margin: 0,
			display: isCollapsed ? "none" : "block",
		},
		toggleButton: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "4px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			borderRadius: "4px",
			transition: "background-color 0.2s ease",
			color: "white",
		},
		closeButton: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "4px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			borderRadius: "4px",
			transition: "background-color 0.2s ease",
			color: "white",
			marginLeft: "8px",
		},
		content: {
			opacity: isCollapsed ? 0 : 1,
			visibility: isCollapsed ? "hidden" : "visible",
			transition: "opacity 0.2s ease",
			padding: isCollapsed ? 0 : "16px",
			flex: 1,
			overflowY: "auto",
			overflowX: "hidden",
		},
		infoGroup: {
			marginBottom: "16px",
			borderBottom: "1px solid #e0e0e0",
			paddingBottom: "12px",
			"&:last-child": {
				borderBottom: "none",
				marginBottom: "0",
			},
		},
		infoLabel: {
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Bold",
			color: "#666",
			marginBottom: "4px",
			textTransform: "uppercase",
			letterSpacing: "0.5px",
		},
		infoValue: {
			fontSize: "14px",
			fontFamily: "MAN EUROPE_Regular",
			color: "#333",
			wordBreak: "break-word",
			lineHeight: "1.4",
		},
		worklogTable: {
			width: "100%",
			borderCollapse: "collapse",
			marginTop: "8px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Regular",
		},
		worklogHeader: {
			backgroundColor: "#1e2832",
			color: "white",
		},
		worklogHeaderCell: {
			padding: "8px 4px",
			textAlign: "left",
			fontFamily: "MAN EUROPE_Bold",
			fontSize: "11px",
			textTransform: "uppercase",
			letterSpacing: "0.5px",
		},
		worklogRow: {
			borderBottom: "1px solid #e0e0e0",
		},
		worklogCell: {
			padding: "6px 4px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Regular",
			color: "#333",
		},
		worklogButton: {
			background: "#1e2832",
			color: "white",
			border: "none",
			borderRadius: "4px",
			padding: "8px 12px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Bold",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			gap: "6px",
			transition: "background-color 0.2s ease",
			marginTop: "8px",
		},
		worklogButtonHover: {
			backgroundColor: "#2a3540",
		},
		worklogSection: {
			marginTop: "12px",
			borderTop: "1px solid #e0e0e0",
			paddingTop: "12px",
		},
		fieldName: {
			fontSize: "10px",
			fontFamily: "MAN EUROPE_Bold",
			color: "#666",
			textTransform: "capitalize",
		},
	};

	const handleCollapsedClick = () => {
		if (isCollapsed) {
			setIsCollapsed(false);
		}
	};

	return (
		<div
			className="side-panel-container"
			data-tour-target="serie-sidepanel"
			style={styles.container}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onClick={handleCollapsedClick}
		>
			<div style={styles.header}>
				{!isCollapsed && <h3 style={styles.title}>Series Info Panel</h3>}
				<div style={{ display: "flex", alignItems: "center" }}>
					<button
						style={styles.toggleButton}
						onClick={toggleCollapse}
						title={isCollapsed ? "Expand panel" : "Collapse panel"}
						aria-label={isCollapsed ? "Expand panel" : "Collapse panel"}
					>
						{isCollapsed ? (
							<ChevronLeft style={{ width: "20px", height: "20px" }} />
						) : (
							<ChevronRight style={{ width: "20px", height: "20px" }} />
						)}
					</button>
					{!isCollapsed && (
						<button
							style={styles.closeButton}
							onClick={handleClose}
							title="Close panel"
							aria-label="Close panel"
							data-tour-target="serie-sidepanel-close"
						>
							<X style={{ width: "16px", height: "16px" }} />
						</button>
					)}
				</div>
			</div>
			<div
				style={styles.content}
				onClick={(e) => !isCollapsed && e.stopPropagation()}
			>
				{" "}
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>ALG</div>
					<div style={styles.infoValue}>{selectedRow?.alg || "N/A"}</div>
				</div>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>SNR</div>
					<div style={styles.infoValue}>{selectedRow?.SNR || "N/A"}</div>
				</div>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Description</div>
					<div style={styles.infoValue}>
						{selectedRow?.description || "N/A"}
					</div>
				</div>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>SE-Team</div>
					<div style={styles.infoValue}>{selectedRow?.seTeam || "N/A"}</div>
				</div>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Special Release Transaction No</div>
					<div style={styles.infoValue}>
						{selectedRow?.deviations?.find(
							(d) => d.type === "Special release transaction no",
						)?.value || "N/A"}
					</div>
				</div>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Project</div>
					<div style={styles.infoValue}>{selectedRow?.project || "N/A"}</div>
				</div>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Change No</div>
					<div style={styles.infoValue}>{selectedRow?.changeNo || "N/A"}</div>
				</div>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Supplier</div>
					<div style={styles.infoValue}>{selectedRow?.supplier || "N/A"}</div>
				</div>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Purchase Type</div>
					<div style={styles.infoValue}>
						{selectedRow?.purchaseType || "N/A"}
					</div>
				</div>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Procurement Responsible</div>
					<div style={styles.infoValue}>
						{selectedRow?.procResponsible || "N/A"}
					</div>{" "}
				</div>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>PPAP Status</div>
					<div style={styles.infoValue}>
						{selectedRow?.deviations?.find((d) => d.type === "Deviation")
							?.value || "N/A"}
					</div>
				</div>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Special Release Needed</div>
					<div style={styles.infoValue}>
						{selectedRow?.deviations?.find(
							(d) => d.type === "Special release needed",
						)?.value || "N/A"}
					</div>
				</div>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Special Release Until</div>
					<div style={styles.infoValue}>
						{selectedRow?.deviations?.find(
							(d) => d.type === "Special release until",
						)?.value || "N/A"}
					</div>
				</div>{" "}
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>MET Date</div>
					<div style={styles.infoValue}>
						{selectedRow?.dates?.find((d) => d.label === "MET")?.value || "N/A"}
					</div>
				</div>{" "}
				{/* Part Entry Date */}
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Part Entry Date</div>
					<div style={styles.infoValue}>
						{selectedRow?.part_entry_date || "N/A"}
					</div>
				</div>
				{/* Tags Section */}
				<div style={{ ...styles.infoGroup, marginTop: "12px" }}>
					<div style={styles.infoLabel}>Tags</div>
					{/* Existing tags as chips */}
					<div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "8px" }}>
						{parseTags(selectedRow?.tags || "").map((tag) => (
							<span
								key={tag}
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: "4px",
									backgroundColor: "#1e2832",
									color: "white",
									borderRadius: "12px",
									padding: "2px 8px",
									fontSize: "11px",
									fontFamily: "MAN EUROPE_Regular",
								}}
							>
								{tag}
								<button
									onClick={() => handleRemoveTag(tag)}
									disabled={savingTags}
									style={{
										background: "none",
										border: "none",
										color: "white",
										cursor: savingTags ? "not-allowed" : "pointer",
										padding: "0",
										lineHeight: 1,
										fontSize: "13px",
										opacity: savingTags ? 0.5 : 1,
									}}
									title={`Remove tag "${tag}"`}
								>
									×
								</button>
							</span>
						))}
						{parseTags(selectedRow?.tags || "").length === 0 && (
							<span style={{ fontSize: "12px", color: "#999", fontFamily: "MAN EUROPE_Regular" }}>
								No tags yet
							</span>
						)}
					</div>
					{/* Add new tag input */}
					<div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
						<input
							type="text"
							value={tagsInput}
							onChange={(e) => setTagsInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleAddTag();
								}
							}}
							placeholder="Add a tag…"
							disabled={savingTags}
							style={{
								flex: 1,
								padding: "4px 8px",
								border: "1px solid #ccc",
								borderRadius: "4px",
								fontSize: "12px",
								fontFamily: "MAN EUROPE_Regular",
								outline: "none",
							}}
						/>
						<button
							onClick={handleAddTag}
							disabled={savingTags || !tagsInput.trim()}
							style={{
								background: "#1e2832",
								color: "white",
								border: "none",
								borderRadius: "4px",
								padding: "4px 10px",
								fontSize: "12px",
								fontFamily: "MAN EUROPE_Bold",
								cursor: savingTags || !tagsInput.trim() ? "not-allowed" : "pointer",
								opacity: savingTags || !tagsInput.trim() ? 0.6 : 1,
							}}
						>
							{savingTags ? "…" : "Add"}
						</button>
					</div>
				</div>
				{/* Worklog Section */}
				<div style={styles.worklogSection}>
					<button style={styles.worklogButton} onClick={handleToggleWorklogs}>
						<Clock style={{ width: "16px", height: "16px" }} />
						Show Worklogs
					</button>
				</div>
			</div>
			{/* Worklogs Modal */}{" "}
			<CraftModal
				show={worklogModalOpen}
				allowClickOut={true}
				onCraftClose={() => setWorklogModalOpen(false)}
			>
				<CraftModalHeader
					header="Field Tracking Worklogs"
					content={`Worklog history for SNR: ${selectedRow?.SNR || ""}`}
				/>
				<CraftModalBody>
					<div style={{ width: "600px", height: "400px", overflow: "auto" }}>
						{loadingFieldTracking ? (
							<div
								style={{ textAlign: "center", padding: "20px", color: "#666" }}
							>
								Loading worklogs...
							</div>
						) : fieldTrackingData.length > 0 ? (
							<table style={styles.worklogTable}>
								<thead style={styles.worklogHeader}>
									<tr>
										<th style={styles.worklogHeaderCell}>Date</th>
										<th style={styles.worklogHeaderCell}>Username</th>
										<th style={styles.worklogHeaderCell}>Field</th>
										<th style={styles.worklogHeaderCell}>Value</th>
									</tr>
								</thead>
								<tbody>
									{fieldTrackingData.map((record, index) => (
										<tr key={index} style={styles.worklogRow}>
											<td style={styles.worklogCell}>
												{record.time_track
													? new Date(record.time_track).toLocaleString()
													: "N/A"}
											</td>
											<td style={styles.worklogCell}>
												{record.username_track || "N/A"}
											</td>
											<td style={styles.worklogCell}>
												<div style={styles.fieldName}>
													{record.track_field?.replace(/_/g, " ") || "N/A"}
												</div>
											</td>
											<td style={styles.worklogCell}>
												{record.track_value || "-"}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						) : (
							<div
								style={{ textAlign: "center", padding: "20px", color: "#666" }}
							>
								No worklog entries found for this item.
							</div>
						)}
					</div>
				</CraftModalBody>
				<CraftModalFooter>
					<CraftButton
						variant="primary"
						size="m"
						type="button"
						onClick={() => setWorklogModalOpen(false)}
					>
						Close
					</CraftButton>
				</CraftModalFooter>
			</CraftModal>
		</div>
	);
};

export default SerieDataTableSidePanel;
